



































































import {
  DrivewealthAccountManagementType,
  InstrumentStatus
} from "@/shared/types";
import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import RiskDisclaimer from "@/shared/components/investments/drivewealth/RiskDisclaimer.vue";
import SharePriceTable from "@/employee/components/investments/drivewealth/SharePriceTable.vue";
import { formatCurrency } from "@/shared/currency";
import * as RouteNames from "@/employee/route-names";
import * as SharedRoutes from "@/shared/route-names";
import { Validator } from "vee-validate";
import { useNotifierStore } from "@/shared/store/notifier";
import { useDrivewealthStore } from "@/shared/store/drivewealth";

interface RequestOrderData {
  shouldSellAll: boolean;
  amountUsd: number;
  isSubmitting: boolean;
}

@Component({
  components: {
    GenericLoadingSpinner,
    SharePriceTable,
    RiskDisclaimer,
  }
})
export default class RequestBuyPage extends Vue {
  @Prop() symbol: string;
  notifierStore = useNotifierStore();

  @Prop() accountId: number;

  data?: RequestOrderData = null;
  loadedStockSymbol?: string = null;

  drivewealthStore = useDrivewealthStore();

  formatCurrency = formatCurrency;
  $validator: Validator;


  created () {
    if (this.accountId) {
      this.drivewealthStore.getAccountSummaryV2({ username: null, partnerId: null, dwAccountId: this.accountId });
    } else {
      this.drivewealthStore.loadDefaultAccount();
      this.drivewealthStore.loadAccountSummary();
    }
    this.onCreatedAndLoaded();
    this.drivewealthStore.loadHsaBalanceAndThreshold({ force: true, accountId: this.accountId });
  }

  get isManagedAccount () {
    return this.drivewealthStore.isAccountManagementType(
        DrivewealthAccountManagementType.RiaManaged, this.accountId);
  }

  get isLoading () {
    return !this.isLoaded;
  }

  get isLoaded () {
    const accountSummaryV2Loaded = this.accountId ?
      this.drivewealthStore.accountSummaryV2Loaded(this.accountId) :
      this.drivewealthStore.accountSummaryLoaded;

    const instrumentDataLoaded = this.isManagedAccount ? true : this.drivewealthStore.instrumentDataIsLoaded(this.symbol);
    return this.drivewealthStore.drivewealthLoaded &&
        !!this.data &&
        this.drivewealthStore.isHsaBalanceAndThresholdLoaded(this.accountId) &&
        instrumentDataLoaded &&
        accountSummaryV2Loaded;
  }

  @Watch("symbol")
  getDataOnSymbolChange () {
    if (this.data && this.loadedStockSymbol !== this.symbol) {
      this.data = null; // the symbol changed, gotta start over!
      this.onCreatedAndLoaded(); // N.B. Setting data = null stops this from being infinite loop
    }

    this.loadedStockSymbol = this.symbol;
    this.data = {
      shouldSellAll: false,
      amountUsd: 0,
      isSubmitting: false
    };
  }

  @Watch("drivewealthStore.drivewealthLoaded")
  onCreatedAndLoaded () {
    if (!this.drivewealthStore.drivewealthLoaded) {
      return;
    }

    if (!this.isManagedAccount) {
      this.drivewealthStore.loadInstrumentData(this.symbol, this.accountId);
    }

    this.getDataOnSymbolChange();
  }

  get heading () {
    return this.drivewealthStore.instrumentHeading(this.symbol, this.accountId);
  }

  get subheading () {
    return this.drivewealthStore.instrumentSubheading(this.symbol, this.accountId);
  }

  get availableToInvest () {
    return this.drivewealthStore.isHsaBalanceAndThresholdLoaded(this.accountId)
        ? this.drivewealthStore.hsaBalanceBeyondThreshold(this.accountId) : 0;
  }

  get canBuy () {
    if (this.isManagedAccount) {
      return !this.drivewealthStore.hasPendingManagedLiquidation;
    }

    const detail = this.drivewealthStore.instrumentDetailBySymbol(this.symbol);
    return detail && detail.status === InstrumentStatus.Active;
  }

  get disableAmount () {
    return !this.canBuy || this.insufficientFundsForBuy;
  }

  get minAmount () {
    return 1; // must buy at least $1.00
  }

  get amountValidator () {
    return {
      required: true,
      decimal: 2,
      min_value_usd: this.minAmount,
      max_value_usd: this.availableToInvest
    };
  }

  get insufficientFundsForBuy () {
    return this.minAmount > this.availableToInvest;
  }

  get hasAmountError () {
    return this.errors.has("amount") || this.insufficientFundsForBuy;
  }

  get amountErrorText () {
    if (this.insufficientFundsForBuy) {
      return "You don't have enough money to invest right now";
    }
    return this.errors.first("amount");
  }

  onCancel () {
    this.navigateBack();
  }

  navigateBack () {
    window.history.length > 1 ? this.$router.go(-1) : this.$router.push("/");
  }

  navigateOnBuyRequested () {
    if (this.accountId) {
      this.$router.push({
        name: SharedRoutes.DrivewealthInvestmentAccountV2,
        params: { accountId: `${this.accountId}`},
        query: { force: "true" }
      });
    } else {
      this.$router.push({ name: RouteNames.InvestmentAccountInfo, query: { force: "true" } });
    }
  }

  onConfirm () {
    if (this.data.isSubmitting) {
      return;
    }

    this.$validator.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.data.isSubmitting = true;
      this.onConfirmAndValidated();
    });
  }

  onConfirmAndValidated () {
    let orderPromise: Promise<any>;

    if (this.isManagedAccount) {
      if (this.accountId) {
        orderPromise = this.drivewealthStore.createManagedDepositByAccount(
            { amount: this.data.amountUsd, accountId: this.accountId });
      } else {
        orderPromise = this.drivewealthStore.createManagedDeposit(this.data.amountUsd);
      }
    } else {
      if (this.accountId) {
        orderPromise = this.drivewealthStore.createOrderRequestByAccount({
          data: {
            side: "BUY",
            units: "USD",
            stockSymbol: this.symbol,
            amount: this.data.amountUsd
          },
          accountId: this.accountId
        });
      } else {
        orderPromise = this.drivewealthStore.createOrderRequest({
          side: "BUY",
          units: "USD",
          stockSymbol: this.symbol,
          amount: this.data.amountUsd
        });
      }
    }

    orderPromise.then(() => {
      const message = `Purchase of ${formatCurrency(this.data.amountUsd)} ${this.symbol} sent`
          + "<div>It may take up to 2 minutes for the order to display.</div>";
      this.notifierStore.setSuccess(message);
      this.navigateOnBuyRequested();
    }).finally(() => {
      this.data.isSubmitting = false;
    });
  }
}
